/* eslint-disable no-console */
import { productByHandleGet } from 'utils/storefront/queries';
import { getBundleProducts } from 'utils/storefront/get-bundle-products';
import { formatProduct } from 'utils/storefront/format-resource';
import setAttributes from 'utils/set-attributes';
import Storefront from 'utils/storefront';

window.getPayload = (variant, qty) => ({
  merchandiseId: variant.id.toString().includes('gid:')
    ? variant.id
    : `gid://shopify/ProductVariant/${variant.id}`,
  qty,
});

window.getBundlePayload = (bundle, product, variant, qty) => {
  const bundleProductsId = `${
    variant.id.split('/ProductVariant/')[1]
  }/${bundle.activeVariants
    .map((v) => v.id.split('/ProductVariant/')[1])
    .join('/')}`;

  return bundle.activeVariants.map((v, index) => {
    const attributes = [
      { key: '_bundleId', value: `${product.id.split('/Product/')[1]}` },
      { key: '_bundleProductsId', value: `${bundleProductsId}` },
      {
        key: '_bundleData',
        value: `${JSON.stringify({
          merchandise: {
            product: {
              ...product,
              handle: product.handle,
              title: product.title,
              image: product.images[0],
              sku: variant.sku,
            },
          },
          cost: {
            totalAmount: product.priceRange.minVariantPrice,
          },
        })}`,
      },
    ];

    if (bundle.products[index].subtitle) {
      attributes.push({
        key: '_short_description',
        value: bundle.products[index].subtitle,
      });
    }

    return {
      merchandiseId: v.id,
      qty,
      attributes,
    };
  });
};

/**
 * Public quick view opener for third parties (e.g. Nosto) and PLP product cards.
 * Dispatches the `quick-view:open` event the QuickView component listens for.
 * Only the product handle is required.
 * @param {string} handle Product handle
 * @param {Object} [options]
 * @param {string} [options.viewType='default'] Quick view layout variant ('default' | 'bundle')
 * @param {Object} [options.meta={}] Extra context forwarded to the quick view
 * @returns {boolean} true when dispatched, false when no handle provided
 */
window.openQuickView = (handle, { viewType = 'default', meta = {} } = {}) => {
  if (!handle) {
    console.warn('product handle required to open quick view');
    return false;
  }

  document.dispatchEvent(
    new CustomEvent('quick-view:open', {
      detail: { payload: handle, viewType, meta },
    }),
  );

  return true;
};

/**
 * Global ATC functionality to expose ATC to third parties
 * @param {Object} params
 * @param {string} params.handle Product handle
 * @param {Integer|string} [params.quantity=1] quantity to add to cart.
 * @returns null
 */

window.globalAddToCart = async ({ handle, quantity = 1 }) => {
  if (!handle) {
    console.warn('product handle required to add to cart');
    return null;
  }
  const storefront = Storefront.getInstance();
  let productPayload;

  const productData = await storefront.request({
    query: productByHandleGet,
    variables: {
      handle,
    },
  });

  if (!productData.data?.productByHandle) {
    console.warn(`Product with handle "${handle}" not found`);
    return null;
  }

  const product = formatProduct(productData?.data?.productByHandle);

  const isBundle = product.tags.find((tag) => tag === 'bundle');
  const variant = product.variants[0];
  const attributes = setAttributes(product);

  if (!variant) {
    console.warn(`Variant not found in ${product.title}`);
    return null;
  }

  if (isBundle) {
    const bundle = await getBundleProducts(product);

    productPayload = window.getBundlePayload(
      bundle,
      product,
      variant,
      quantity,
    );
  } else {
    productPayload = {
      ...window.getPayload(variant, quantity),
      attributes,
    };
  }

  const addToCartEvent = new CustomEvent('cart:add', {
    detail: {
      payload: productPayload,
    },
  });
  document.dispatchEvent(addToCartEvent);

  return null;
};

window.nostoAddBundleToCart = async ({
  items,
  discount,
  bundleProducts,
  hash,
}) => {
  const payload = items.map((item) => ({
    id: item.variantId,
    quantity: item.quantity,
    attributes: [
      {
        key: '_nosto_discount_value',
        value: `${discount.value}`,
      },
      {
        key: '_nosto_discount_type',
        value: discount.type,
      },
      {
        key: '_nosto_hash',
        value: hash,
      },
      {
        key: '_nosto_products',
        value: bundleProducts.join(';'),
      },
    ],
  }));

  const addToCartEvent = new CustomEvent('cart:add', {
    detail: {
      payload,
    },
  });
  document.dispatchEvent(addToCartEvent);

  return null;
};


window.decodeHtml = (str = '') =>
  new DOMParser().parseFromString(str, 'text/html').documentElement.textContent ?? ''
